<template lang="pug">
.send-request-histories-tab
  .mb-3

  .history-period.d-flex.flex-row.align-items-center
    .title.mr-2 履歴範囲
    b-form-select.history-option#send-request-history-period(v-model="historyPeriod" :options="historyPeriodOptions" size="md" @change="changeHistoryPeriod")
  .mb-2
  
  SendRequestHistoriesTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    @onChangeEnded="onChangeEnded"
    @onRequestDetail="onRequestDetail"
    @onRepliesDetail="onRepliesDetail"
  )
  .table-options.d-flex.flex-row.align-items-center
    .d-flex.flex-row.flex-fill.justify-content-end.align-items-center
      //- 検索
      .search-filter.d-flex.mr-1.align-items-center.mr-2
        b-input-group.search-filter-input-group.mr-1(size="md")
          b-input-group-prepend
            b-input-group-text.search-filter-icon
              .search-icon
          b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
        HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="send-request-histories-tab-filter-help" title="検索項目について")
          template(v-slot:text)
            div 検索項目は以下になります。
            div 利用者（患者）名
      //- ページあたりの行数
      span.table-option-text 1ページの行数:
      .per-page
        b-form-select(
          id="family-per-page-select"
          v-model="perPage"
          :options="pageOptions"
          size="md"
          @change="selectPerPage"
        )
      //- ページネーション
      b-pagination.m-0(
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        :disabled="isLoading"
        @change="selectPage"
        hide-goto-end-buttons
      )

  //- リクエスト詳細モーダル
  RequestDetailModal(v-model="showRequestDetailModal" :request="requestDetailModalTargetRequest" :isSender="true")
</template>

<script lang="ts">
import { Component, Watch, Inject, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import Setting from "../../../../public/setting.json";

import SystemError from "@/commons/models/SystemError";

import OtherOfficeAcceptanceRequestsService, { OtherOfficeAcceptanceRequestsGetModel } from "@/commons/services/OtherOfficeAcceptanceRequests";

import SendRequestHistoriesTable, {
  SendRequestHistoriesTableField,
  SendRequestHistoriesTableItem,
  FilterSetting
} from "@/pc/containers/otherOfficeRoomSchedules/SendRequestHistoriesTable.vue";
import RequestDetailModal from "@/pc/components/otherOfficeRoomSchedules/RequestDetailModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";

/**
 * ### 送信タブコンポーネント
 */
@Component({
  components: {
    SendRequestHistoriesTable,
    RequestDetailModal,
    HelpIcon
  }
})
export default class SendRequestHistoriesTab extends Vue {
  /** 受入 リクエストサービス */
  @Inject("OtherOfficeAcceptanceRequestsService") requestsService!: OtherOfficeAcceptanceRequestsService;

  /** 履歴範囲 */
  historyPeriod: number = HistoryPeriodOptionValue.ONE_MONTH;
  /** 履歴範囲選択肢 */
  historyPeriodOptions: Array<{ value: number | string; text: string }> = [
    { value: HistoryPeriodOptionValue.ONE_MONTH, text: "1ヵ月" },
    { value: HistoryPeriodOptionValue.THREE_MONTH, text: "3ヵ月" },
    { value: HistoryPeriodOptionValue.SIX_MONTH, text: "6ヵ月" },
    { value: HistoryPeriodOptionValue.ONE_YEAR, text: "1年" },
    { value: HistoryPeriodOptionValue.ALL, text: "全て" }
  ];

  /** メッセージ履歴列名 */
  fields: Array<SendRequestHistoriesTableField> = [];
  /** メッセージ履歴の情報 */
  items: Array<SendRequestHistoriesTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** リクエスト詳細表示モーダル表示可否 */
  showRequestDetailModal: boolean = false;
  /** リクエスト詳細表示モーダルに渡すリクエスト */
  requestDetailModalTargetRequest: SendRequestHistoriesTableItem | null = null;

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.otherOffice.roomSchedule.sendRequestTab.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.otherOffice.roomSchedule.sendRequestTab.pageOptions;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  private setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `other-office-room-schedule-send-request-history-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SendRequestHistoriesTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.sendMessage.perPage);

    await this.createView();

    return;
  }

  /**
   * ### メッセージ履歴の列名を作成する
   */
  private createFields(): Array<SendRequestHistoriesTableField> {
    const fields: Array<SendRequestHistoriesTableField> = [];
    fields.push({ key: "ended", label: "状態", sortable: false, sortDirection: "desc", class: "ended text-center" });
    fields.push({ key: "datetime", label: "送信日時", sortable: false, sortDirection: "desc", class: "datetime text-center" });
    fields.push({ key: "residentName", label: "利用者（患者）名", sortable: false, sortDirection: "desc", class: "resident-name" });
    fields.push({ key: "requestDetail", label: "送信内容", sortable: false, sortDirection: "desc", class: "request-detail text-center" });
    fields.push({ key: "replyAcceptance", label: "入所可否確認", sortable: false, sortDirection: "desc", class: "reply-acceptance" });
    fields.push({ key: "replyDetail", label: "返信状況", sortable: false, sortDirection: "desc", class: "reply-detail text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  private loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["other-office-room-schedule-send-request-history-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### テーブルで表示する行情報と行数を取得する
   */
  private async createView(): Promise<void> {
    this.isLoading = true;

    this.items = [];
    this.currentPage = 1;

    if (this.historyPeriod == HistoryPeriodOptionValue.ALL) {
      await this.setRows(undefined);

      const records: Array<OtherOfficeAcceptanceRequestsGetModel> = await this.getRequests(undefined);
      this.items = records;
    } else {
      let today: Date = new Date();
      const end: string = this.$dateUtil.convertDateToString(today, "yyyymmdd235959000");

      switch (this.historyPeriod) {
        case HistoryPeriodOptionValue.ONE_MONTH: {
          today.setMonth(today.getMonth() - 1);
          break;
        }
        case HistoryPeriodOptionValue.THREE_MONTH: {
          today.setMonth(today.getMonth() - 3);
          break;
        }
        case HistoryPeriodOptionValue.SIX_MONTH: {
          today.setMonth(today.getMonth() - 6);
          break;
        }
        case HistoryPeriodOptionValue.ONE_YEAR: {
          today.setFullYear(today.getFullYear() - 1);
          break;
        }
        default: {
          today.setDate(today.getDate() - 7);
          break;
        }
      }
      const start: string = this.$dateUtil.convertDateToString(today, "yyyymmdd000000000");
      await this.setRows({ start, end });

      const records: Array<OtherOfficeAcceptanceRequestsGetModel> = await this.getRequests({ start, end });
      this.items = records;
    }

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  private async setRows(params: { start: string; end: string } | undefined): Promise<void> {
    let queryParams: any = {
      type: "HEAD"
    };
    if (params) {
      queryParams.start = params.start;
      queryParams.end = params.end;
    }
    const count: number = await this.requestsService.getCount(userStore, queryParams);
    this.rows = count;
    return;
  }

  /**
   * ### メッセージ履歴を取得するAPI呼び出し
   */
  private async getRequests(params: { start: string; end: string } | undefined): Promise<Array<OtherOfficeAcceptanceRequestsGetModel>> {
    let response: Array<OtherOfficeAcceptanceRequestsGetModel> = [];

    try {
      let queryParams: any = {
        type: "HEAD"
      };
      if (params) {
        queryParams.start = params.start;
        queryParams.end = params.end;
      }
      response = await this.requestsService.gets(userStore, queryParams);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        let queryParams: any = {
          type: "HEAD"
        };
        if (params) {
          queryParams.start = params.start;
          queryParams.end = params.end;
        }
        const count: number = await this.requestsService.getCount(userStore, queryParams);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          queryParams.startIndex = i * pagePerCount;
          queryParams.endIndex = i * pagePerCount + pagePerCount;
          queryParams.order = "desc";
          response = response.concat(await this.requestsService.gets(userStore, queryParams));
        }
      }
    }
    return response;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   */
  selectPerPage(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  selectPage(page: number): void {
    this.currentPage = page;
    return;
  }

  /**
   * ### 状態が変更されたことを親コンポーネントに通知する
   */
  onChangeEnded(item: SendRequestHistoriesTableItem): void {
    this.$emit("onChangeEnded", item);
    return;
  }

  /**
   * ### リクエスト詳細をモーダルで表示する
   */
  onRequestDetail(item: SendRequestHistoriesTableItem): void {
    this.requestDetailModalTargetRequest = item;
    this.showRequestDetailModal = true;
    return;
  }

  /**
   * ### リクエスト返信の表示を親コンポーネントに通知する
   */
  onRepliesDetail(item: SendRequestHistoriesTableItem): void {
    this.$emit("onRepliesDetail", item);
    return;
  }

  /**
   * ### 画面の作り直し
   *
   * - 親コンポーネントから呼び出される
   */
  public async refresh(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面の作り直し
   *
   * - TODO 将来リフレッシュボタンが追加された場合に使用する
   */
  // public async refreshByNoLoading(): Promise<void> {
  //   try {
  //     await this.createView();
  //     await this.setFamilyUnreadByCurrentPage();
  //   } catch (error) {
  //     // 処理中に画面遷移が発生した場合、エラーと見なさないためURLチェックする
  //     if (this.$route.fullPath != PcUrl.SendMessage) {
  //       return;
  //     } else {
  //       throw error;
  //     }
  //   }
  //   return;
  // }

  /**
   * ### 期間変更のセレクトボックス値変更イベント
   *
   * - 履歴を取得しなおす
   */
  async changeHistoryPeriod(): Promise<void> {
    await this.createView();
    return;
  }
}

export enum HistoryPeriodOptionValue {
  UNKNOWN = 0,
  ONE_MONTH = 1,
  THREE_MONTH = 2,
  SIX_MONTH = 3,
  ONE_YEAR = 4,
  ALL = 5
}
</script>

<style lang="scss" scoped>
.send-request-histories-tab {
  .history-period {
    .title {
      font-weight: bold;
    }
    .history-option {
      width: 120px;
    }
  }
}
</style>
